/**
 * confirmDialog — an imperative, promise-returning replacement for
 * window.confirm(), rendered with the app's own ConfirmDialog.
 *
 * ConfirmDialog itself is declarative (open + onConfirm/onCancel), which is
 * right for a dialog a screen owns but wrong for the ~26 scattered
 * "are you sure?" checks: each would need its own piece of state, a pending
 * action stashed somewhere, and a handler pair. This keeps the call site a
 * single line, so replacing a native confirm is a local edit:
 *
 *     if (!window.confirm("Delete this?")) return;      // before
 *     if (!(await confirmDialog("Delete this?"))) return;   // after
 *
 * Pass a string for the simple case, or an options object for a title, a
 * red confirm button, or custom labels:
 *
 *     await confirmDialog({
 *       title: "Delete shift",
 *       message: "This cannot be undone.",
 *       confirmLabel: "Delete",
 *       danger: true,
 *     })
 *
 * Mount <ConfirmHost /> once, at the app root (see App.js).
 */
import React, { useCallback, useEffect, useState } from "react";
import { ConfirmDialog } from "./ui";

// Set by the mounted host. Module-level rather than context so the helper can
// be called from anywhere — including plain async handlers and non-component
// modules — without threading a hook through every caller.
let openConfirm = null;

export const confirmDialog = (options) => {
  const opts = typeof options === "string" ? { message: options } : options || {};
  if (typeof openConfirm !== "function") {
    /* No host mounted — a unit test, or a tree rendered without ConfirmHost.
       Fall back to the browser dialog rather than resolving true or false on
       its own: resolving true would let a destructive action run unconfirmed,
       and resolving false would make the button look broken. */
    // eslint-disable-next-line no-alert
    return Promise.resolve(window.confirm(opts.message || "Are you sure?"));
  }
  return openConfirm(opts);
};

export const ConfirmHost = () => {
  // { opts, resolve } while a confirm is open, null otherwise.
  const [pending, setPending] = useState(null);

  useEffect(() => {
    openConfirm = (opts) => new Promise((resolve) => setPending({ opts, resolve }));
    return () => {
      openConfirm = null;
    };
  }, []);

  // Settle the promise BEFORE clearing, so a caller awaiting it always gets an
  // answer even if this unmounts in the same tick.
  const settle = useCallback(
    (answer) => {
      setPending((cur) => {
        cur?.resolve(answer);
        return null;
      });
    },
    []
  );

  const opts = pending?.opts || {};
  return (
    <ConfirmDialog
      open={!!pending}
      title={opts.title}
      message={opts.message}
      confirmLabel={opts.confirmLabel}
      cancelLabel={opts.cancelLabel}
      danger={opts.danger}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );
};

export default confirmDialog;
